import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/ui/field";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Search, Plus, MoreHorizontal, Pencil, Power, Users, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import {
  employeesApi, Employee, CreateEmployeeBody, ListEmployeesQuery, EmployeeStatus, EmployeeSummary,
} from "@/lib/employees-api";
import { sitesApi, SiteSummary } from "@/lib/sites-api";
import { departmentsApi, Department } from "@/lib/departments-api";
import { departmentOptions, pickedIds, siteOptions } from "@/lib/picker-options";
import { SearchSelect } from "@/components/SearchSelect";
import { SearchMultiSelect } from "@/components/SearchMultiSelect";
import { ApiError } from "@/lib/api";
import { BulkEmployeeImportDialog } from "@/components/BulkEmployeeImportDialog";
import { EmployeePicker } from "@/components/EmployeePicker";
import { ListPager } from "@/components/ListPager";
import { InviteLinkDialog } from "@/components/InviteLinkDialog";
import { invitePrompt, resetPrompt } from "@/lib/mobile-access";
import { formatDateTimeIn } from "@/lib/datetime";
import { validateRequired } from "@/lib/form-validation";
import { describeShift, shiftsApi, type Shift } from "@/lib/shifts-api";
import { useTenantTimezone } from "@/hooks/useTenantTimezone";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { usePage } from "@/hooks/usePage";

const STATUS_OPTIONS: { value: EmployeeStatus; label: string }[] = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

/**
 * Rows per page. The table used to fetch one page of 200 and treat it as
 * everyone — with 1,824 staff, rows 201+ were unreachable and the cards read
 * "Total 200" (2026-10-29). It now pages, and the cards come from the API.
 */
const PAGE_SIZE = 50;

const emptyForm: CreateEmployeeBody = {
  empCode: "",
  name: "",
  email: "",
  phone: "",
  departmentId: undefined,
  role: "",
  primarySiteId: undefined,
  shiftId: undefined,
  managerId: undefined,
  status: "ACTIVE",
};

const Employees = () => {
  const tz = useTenantTimezone();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<EmployeeSummary | null>(null);
  const [sites, setSites] = useState<SiteSummary[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const siteChoices = useMemo(() => siteOptions(sites), [sites]);
  const deptFilterChoices = useMemo(() => departmentOptions(departments, { includesSubunits: true }), [departments]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  // Seed from the header's global search (`?q=`), if present.
  const { search, setSearch, debouncedSearch } = useDebouncedSearch(
    new URLSearchParams(window.location.search).get("q") ?? "",
  );
  const [statusFilter, setStatusFilter] = useState<"all" | EmployeeStatus>("all");
  // Several of each may be picked (FEAT-2026-030); empty means all.
  const [deptFilter, setDeptFilter] = useState<string[]>([]);
  const [siteFilter, setSiteFilter] = useState<string[]>([]);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<CreateEmployeeBody>(emptyForm);
  const [addManagerLabel, setAddManagerLabel] = useState<string | null>(null);
  const [addSubmitting, setAddSubmitting] = useState(false);

  const [editing, setEditing] = useState<Employee | null>(null);
  const [editForm, setEditForm] = useState<CreateEmployeeBody>(emptyForm);
  /** The manager's display name — the picker can't find it among the page's rows. */
  const [editManagerLabel, setEditManagerLabel] = useState<string | null>(null);
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [disabling, setDisabling] = useState<Employee | null>(null);
  const [inviting, setInviting] = useState<Employee | null>(null);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [resetting, setResetting] = useState<Employee | null>(null);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  // What each confirmation says and offers — email-only sign-in (ADR-0017).
  const inviteView = inviting ? invitePrompt(inviting) : null;
  const resetView = resetting ? resetPrompt(resetting) : null;

  /**
   * Set when an email-path invite returns a Clerk invitation URL — drives
   * the InviteLinkDialog so admins can copy the link if Clerk's email
   * doesn't deliver.
   */
  const [inviteLink, setInviteLink] = useState<{
    name: string;
    email: string;
    url: string;
    variant: "new" | "reset";
  } | null>(null);

  useEffect(() => {
    sitesApi.list({ pageSize: 200 }).then((r) => setSites(r.data)).catch(() => { /* non-fatal */ });
    // Non-fatal: without shifts the picker simply offers "follows the site".
    shiftsApi.list().then(setShifts).catch(() => { /* non-fatal */ });
    // Every department, not one page: a tenant can have more than 200 (KPTM has 250).
    departmentsApi.listAll().then(setDepartments).catch(() => { /* non-fatal */ });
  }, []);

  const filters = useMemo(() => ({
    search: debouncedSearch || undefined,
    status: statusFilter === "all" ? undefined : statusFilter,
    departmentIds: pickedIds(deptFilter),
    primarySiteIds: pickedIds(siteFilter),
  }), [debouncedSearch, statusFilter, deptFilter, siteFilter]);
  // Any filter change goes back to page 1.
  const [page, setPage] = usePage(JSON.stringify(filters));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const q: ListEmployeesQuery = { ...filters, page, pageSize: PAGE_SIZE };
    employeesApi
      .list(q)
      .then((r) => {
        if (!cancelled) {
          setEmployees(r.data);
          setTotal(r.total);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        const msg = e instanceof ApiError ? e.message : "Failed to load employees";
        toast.error(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [filters, page, reloadKey]);

  // The cards count everyone the filters match, not the rows on this page.
  useEffect(() => {
    let cancelled = false;
    employeesApi
      .summary(filters)
      .then((r) => { if (!cancelled) setSummary(r); })
      .catch(() => { if (!cancelled) setSummary(null); /* non-fatal — the cards show a dash */ });
    return () => { cancelled = true; };
  }, [filters, reloadKey]);

  const triggerReload = () => setReloadKey((k) => k + 1);

  const submitCreate = async () => {
    const missing = validateRequired([
      { label: "Employee code", value: addForm.empCode },
      { label: "Full name", value: addForm.name },
    ]);
    if (missing) {
      toast.error(missing);
      return;
    }
    setAddSubmitting(true);
    try {
      const created = await employeesApi.create({
        ...addForm,
        email: addForm.email?.trim() || undefined,
        phone: addForm.phone?.trim() || undefined,
        departmentId: addForm.departmentId || undefined,
        role: addForm.role?.trim() || undefined,
        primarySiteId: addForm.primarySiteId || undefined,
        shiftId: addForm.shiftId || null,
        managerId: addForm.managerId || undefined,
      });
      toast.success(`${created.name} added`, {
        description: "Use the row menu → 'Invite to mobile app' when they're ready to sign in.",
        duration: 6000,
      });
      setAddOpen(false);
      setAddForm(emptyForm);
      setAddManagerLabel(null);
      triggerReload();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Failed to create employee";
      toast.error(msg);
    } finally {
      setAddSubmitting(false);
    }
  };

  const confirmInvite = async () => {
    if (!inviting) return;
    setInviteSubmitting(true);
    try {
      const result = await employeesApi.invite(inviting.id);
      if (result.linkedToExisting) {
        // The employee already has a Clerk identity (e.g. they're also a
        // dashboard user). No invitation; they sign in with their
        // existing password.
        toast.success(`${result.employee.name} linked to mobile app`, {
          description: `Their existing account (${inviting.email}) now also gives them mobile access — they sign in with their current password.`,
          duration: 8000,
        });
      } else if (result.inviteUrl && inviting.email) {
        // Email path — surface the URL so admin can hand-share if needed.
        setInviteLink({
          name: result.employee.name,
          email: inviting.email,
          url: result.inviteUrl,
          variant: "new",
        });
      } else {
        // Edge case: email path but the SDK didn't return a URL. Toast only.
        toast.success(`Invitation sent to ${inviting.email}`, {
          description: `${inviting.name} will receive an email to set up their mobile login.`,
          duration: 6000,
        });
      }
      setInviting(null);
      triggerReload();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Failed to invite employee";
      toast.error(msg);
    } finally {
      setInviteSubmitting(false);
    }
  };

  const openEdit = (e: Employee) => {
    setEditing(e);
    setEditForm({
      empCode: e.empCode,
      name: e.name,
      email: e.email ?? "",
      phone: e.phone ?? "",
      departmentId: e.departmentId ?? undefined,
      role: e.role ?? "",
      primarySiteId: e.primarySiteId ?? undefined,
      shiftId: e.shiftId ?? undefined,
      managerId: e.managerId ?? null,
      status: e.status,
    });
    setEditManagerLabel(e.managerName);
  };

  const submitEdit = async () => {
    if (!editing) return;
    // Same guard as create — without it, clearing a required field surfaced the
    // backend's raw class-validator text ("name must be longer than or equal to
    // 2 characters") instead of naming the field.
    const missing = validateRequired([
      { label: "Employee code", value: editForm.empCode },
      { label: "Full name", value: editForm.name },
    ]);
    if (missing) {
      toast.error(missing);
      return;
    }
    setEditSubmitting(true);
    try {
      await employeesApi.update(editing.id, {
        empCode: editForm.empCode,
        name: editForm.name,
        email: editForm.email?.trim() || undefined,
        phone: editForm.phone?.trim() || undefined,
        departmentId: editForm.departmentId || undefined,
        role: editForm.role?.trim() || undefined,
        primarySiteId: editForm.primarySiteId || undefined,
        shiftId: editForm.shiftId || null,
        // null removes the manager; the form always holds the current one.
        managerId: editForm.managerId ?? null,
        status: editForm.status,
      });
      toast.success("Employee updated");
      setEditing(null);
      triggerReload();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Failed to update employee";
      toast.error(msg);
    } finally {
      setEditSubmitting(false);
    }
  };

  const confirmDisable = async () => {
    if (!disabling) return;
    try {
      await employeesApi.disable(disabling.id);
      toast.success(`${disabling.name} disabled`);
      setDisabling(null);
      triggerReload();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Failed to disable employee";
      toast.error(msg);
    }
  };

  const confirmResetPassword = async () => {
    if (!resetting) return;
    setResetSubmitting(true);
    try {
      const result = await employeesApi.resetMobilePassword(resetting.id);
      if (result.invitationSent) {
        toast.success(`Sessions revoked for ${resetting.name}`, {
          description: `${resetting.name} can use 'Forgot password' on the sign-in screen to set a new password.`,
          duration: 8000,
        });
      }
      setResetting(null);
      triggerReload();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Failed to reset mobile password";
      toast.error(msg);
    } finally {
      setResetSubmitting(false);
    }
  };

  const reactivate = async (e: Employee) => {
    try {
      await employeesApi.update(e.id, { status: "ACTIVE" });
      toast.success(`${e.name} reactivated`);
      triggerReload();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Failed to reactivate employee";
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Total" value={summary?.total ?? null} icon={Users} />
        <StatCard label="Active" value={summary?.active ?? null} tone="success" />
        <StatCard label="Inactive" value={summary?.inactive ?? null} tone="muted" />
        <StatCard label="Mobile app" value={summary?.mobileApp ?? null} tone="info" />
      </div>

      {/* Filter + actions */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs text-muted-foreground">Search</Label>
            <div className="relative mt-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input type="search" name="employee-list-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, email or code…" className="pl-9 h-9" />
            </div>
          </div>
          <div className="w-44">
            <Label className="text-xs text-muted-foreground">Department</Label>
            <SearchMultiSelect
              label="Department"
              values={deptFilter}
              onChange={setDeptFilter}
              options={deptFilterChoices}
              allLabel="All departments"
              noun={{ one: "department", many: "departments" }}
              className="mt-1 h-9"
            />
          </div>
          <div className="w-48">
            <Label className="text-xs text-muted-foreground">Site</Label>
            <SearchMultiSelect
              label="Site"
              values={siteFilter}
              onChange={setSiteFilter}
              options={siteChoices}
              allLabel="All sites"
              noun={{ one: "site", many: "sites" }}
              className="mt-1 h-9"
            />
          </div>
          <div className="w-32">
            <Label className="text-xs text-muted-foreground">Status</Label>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
              <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <BulkEmployeeImportDialog
              onImport={(rows) => employeesApi.bulkImport(rows)}
              onCompleted={triggerReload}
            />
            <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) { setAddForm(emptyForm); setAddManagerLabel(null); } }}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-gradient-primary"><Plus className="mr-1.5 h-3.5 w-3.5" />Add Employee</Button>
              </DialogTrigger>
              <EmployeeDialog
                title="Add new employee"
                description="Provision a workforce record with site, role and reporting manager."
                form={addForm}
                setForm={setAddForm}
                managerLabel={addManagerLabel}
                setManagerLabel={setAddManagerLabel}
                sites={sites}
                shifts={shifts}
                departments={departments}
                submitting={addSubmitting}
                onCancel={() => setAddOpen(false)}
                onSubmit={submitCreate}
                submitLabel="Create employee"
              />
            </Dialog>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-3 text-left font-medium">Employee</th>
                <th className="p-3 text-left font-medium">Department</th>
                <th className="p-3 text-left font-medium">Role</th>
                <th className="p-3 text-left font-medium">Site</th>
                <th className="p-3 text-left font-medium">Manager</th>
                <th className="p-3 text-left font-medium">Status</th>
                <th className="p-3 text-left font-medium">Mobile auth</th>
                <th className="p-3 text-left font-medium">Home</th>
                <th className="p-3 text-left font-medium">Last activity</th>
                <th className="p-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="p-12 text-center text-muted-foreground">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin opacity-60" />
                </td></tr>
              ) : employees.length === 0 ? (
                <tr><td colSpan={10} className="p-12 text-center text-muted-foreground">
                  <Users className="mx-auto mb-2 h-8 w-8 opacity-40" />
                  No employees match your filters.
                </td></tr>
              ) : employees.map((e) => (
                <tr key={e.id} className="border-b last:border-0 hover:bg-secondary/30">
                  <td className="p-3">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-primary text-[10px] font-semibold text-white">
                        {e.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                      </div>
                      <div>
                        <div className="font-medium leading-tight">{e.name}</div>
                        <div className="text-xs text-muted-foreground">{e.empCode}{e.email ? ` · ${e.email}` : ""}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-muted-foreground">{e.departmentPath ?? e.department ?? "—"}</td>
                  <td className="p-3">{e.role ?? "—"}</td>
                  <td className="p-3 text-muted-foreground">{e.primarySiteName ?? "—"}</td>
                  <td className="p-3 text-muted-foreground">{e.managerName ?? "—"}</td>
                  <td className="p-3">
                    <Badge variant="outline" className={e.status === "ACTIVE" ? "border-success/30 text-success bg-success/5" : "border-border text-muted-foreground"}>
                      <span className={`mr-1.5 h-1.5 w-1.5 rounded-full ${e.status === "ACTIVE" ? "bg-success" : "bg-muted-foreground"}`} />
                      {e.status === "ACTIVE" ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td className="p-3">
                    {e.mobileAuthLinked ? (
                      <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5">
                        <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-primary" />
                        Linked
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-border text-muted-foreground">
                        Not invited
                      </Badge>
                    )}
                  </td>
                  <td className="p-3">
                    {e.homeLocationSet ? (
                      <Badge
                        variant="outline"
                        className="border-info/30 text-info bg-info/5"
                        title={e.home?.address ?? undefined}
                      >
                        Set
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {formatDateTimeIn(e.lastActivityAt, tz)}
                  </td>
                  <td className="p-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => openEdit(e)}><Pencil className="mr-2 h-3.5 w-3.5" />Edit employee</DropdownMenuItem>
                        {e.status === "ACTIVE" && !e.mobileAuthLinked && (
                          <DropdownMenuItem onClick={() => setInviting(e)}>
                            <KeyRound className="mr-2 h-3.5 w-3.5" />Invite to mobile app
                          </DropdownMenuItem>
                        )}
                        {e.status === "ACTIVE" && e.mobileAuthLinked && (
                          <DropdownMenuItem onClick={() => setResetting(e)}>
                            <KeyRound className="mr-2 h-3.5 w-3.5" />Reset mobile password
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        {e.status === "ACTIVE" ? (
                          <DropdownMenuItem className="text-destructive" onClick={() => setDisabling(e)}><Power className="mr-2 h-3.5 w-3.5" />Disable</DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => reactivate(e)}><Power className="mr-2 h-3.5 w-3.5" />Reactivate</DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ListPager page={page} pageSize={PAGE_SIZE} total={total} loading={loading} onPageChange={setPage} noun="employees" />
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <EmployeeDialog
          title="Edit employee"
          description="Update employee details, site, manager and status."
          form={editForm}
          setForm={setEditForm}
          managerLabel={editManagerLabel}
          setManagerLabel={setEditManagerLabel}
          sites={sites}
          shifts={shifts}
          departments={departments}
          currentId={editing?.id}
          submitting={editSubmitting}
          onCancel={() => setEditing(null)}
          onSubmit={submitEdit}
          submitLabel="Save changes"
          includeStatus
        />
      </Dialog>

      {/* Disable confirm */}
      <AlertDialog open={!!disabling} onOpenChange={(o) => !o && setDisabling(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable employee?</AlertDialogTitle>
            <AlertDialogDescription>
              {disabling?.name} ({disabling?.empCode}) will be marked inactive. Their attendance history is preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={confirmDisable}>Disable</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* First-time mobile invite */}
      <AlertDialog open={!!inviting} onOpenChange={(o) => !o && !inviteSubmitting && setInviting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Invite {inviting?.name} to the mobile app?</AlertDialogTitle>
            <AlertDialogDescription>{inviteView?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={inviteSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-gradient-primary"
              onClick={inviteView?.canProceed ? confirmInvite : () => inviting && openEdit(inviting)}
              disabled={inviteSubmitting}
            >
              {inviteSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {inviteView?.actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset mobile password confirm */}
      <AlertDialog open={!!resetting} onOpenChange={(o) => !o && !resetSubmitting && setResetting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset mobile password?</AlertDialogTitle>
            <AlertDialogDescription>{resetView?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-gradient-primary"
              onClick={resetView?.canProceed ? confirmResetPassword : () => resetting && openEdit(resetting)}
              disabled={resetSubmitting}
            >
              {resetSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {resetView?.actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invitation URL reveal (email path) */}
      {inviteLink && (
        <InviteLinkDialog
          open={!!inviteLink}
          onClose={() => setInviteLink(null)}
          recipientName={inviteLink.name}
          recipientEmail={inviteLink.email}
          inviteUrl={inviteLink.url}
          variant={inviteLink.variant}
        />
      )}
    </div>
  );
};


const StatCard = ({ label, value, tone = "default" }: { label: string; value: number | null; icon?: unknown; tone?: "default" | "success" | "muted" | "info" }) => {
  const toneClasses =
    tone === "success" ? "text-success" : tone === "info" ? "text-primary" : tone === "muted" ? "text-muted-foreground" : "";
  return (
    <Card className="p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`mt-1 font-display text-2xl font-semibold ${toneClasses}`}>{value === null ? "—" : value.toLocaleString()}</div>
    </Card>
  );
};

interface EmployeeDialogProps {
  title: string;
  description: string;
  form: CreateEmployeeBody;
  setForm: (f: CreateEmployeeBody) => void;
  managerLabel: string | null;
  setManagerLabel: (label: string | null) => void;
  sites: SiteSummary[];
  departments: Department[];
  currentId?: string;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  submitLabel: string;
  includeStatus?: boolean;
  shifts: Shift[];
}

const EmployeeDialog = ({
  title, description, form, setForm, managerLabel, setManagerLabel, sites, shifts, departments, currentId, submitting, onCancel, onSubmit, submitLabel, includeStatus,
}: EmployeeDialogProps) => (
  <DialogContent className="sm:max-w-2xl">
    <DialogHeader>
      <DialogTitle>{title}</DialogTitle>
      <DialogDescription>{description}</DialogDescription>
    </DialogHeader>
    <div className="grid gap-3 sm:grid-cols-2 py-2">
      <Field label="Employee code *">
        <Input name="employee-code" value={form.empCode} onChange={(e) => setForm({ ...form, empCode: e.target.value })} placeholder="GK001" />
      </Field>
      <Field label="Full name *">
        <Input name="employee-full-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Aisyah Aziz" />
      </Field>
      <Field label="Email">
        <Input type="email" name="employee-email" value={form.email ?? ""} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="aisyah@example.com" />
      </Field>
      <Field label="Phone">
        <Input name="employee-phone" value={form.phone ?? ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+60 12-345 6789" />
      </Field>
      <Field label="Department">
        <SearchSelect
          label="Department"
          value={form.departmentId ?? "none"}
          onChange={(v) => setForm({ ...form, departmentId: v === "none" ? undefined : v })}
          options={departmentOptions(departments)}
          emptyOption={{ value: "none", label: "— Unassigned —" }}
        />
      </Field>
      <Field label="Role / designation">
        <Input value={form.role ?? ""} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Field Officer" />
      </Field>
      <Field label="Primary site">
        <SearchSelect
          label="Primary site"
          value={form.primarySiteId ?? "none"}
          onChange={(v) => setForm({ ...form, primarySiteId: v === "none" ? undefined : v })}
          options={siteOptions(sites)}
          emptyOption={{ value: "none", label: "— Unassigned —" }}
        />
      </Field>
      {/* A shift overrides the site's hours and working week (BR-200). Left
          unassigned, the employee simply follows their site — which is the
          right answer for a single-pattern organisation. */}
      <Field
        label="Shift"
        hint="Optional. Overrides the primary site's hours and working days."
      >
        <Select
          value={form.shiftId ?? "none"}
          onValueChange={(v) => setForm({ ...form, shiftId: v === "none" ? undefined : v })}
        >
          <SelectTrigger><SelectValue placeholder="Follows the site…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— Follows the site —</SelectItem>
            {shifts.map((sh) => (
              <SelectItem key={sh.id} value={sh.id}>
                {sh.name} · {describeShift(sh.days)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      {/* Searches the whole tenant: the page only holds one page of staff. */}
      <Field label="Reporting manager">
        <EmployeePicker
          label="Reporting manager"
          value={form.managerId}
          selectedLabel={managerLabel}
          excludeId={currentId}
          onChange={(id, label) => {
            setForm({ ...form, managerId: id });
            setManagerLabel(label);
          }}
        />
      </Field>
      {includeStatus && (
        <Field label="Status">
          <Select value={form.status ?? "ACTIVE"} onValueChange={(v) => setForm({ ...form, status: v as EmployeeStatus })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      )}
    </div>
    <DialogFooter>
      <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancel</Button>
      <Button className="bg-gradient-primary" onClick={onSubmit} disabled={submitting}>
        {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {submitLabel}
      </Button>
    </DialogFooter>
  </DialogContent>
);

export default Employees;
